import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { checkAuth, fetchSettings, logout, updateSettingsWithImage } from '../services/api';
import type { Settings, OrganizationType } from '../types';

interface AppContextValue {
  isAuthenticated: boolean;
  showLoginPopup: boolean;
  organizationName: string;            // 空字串代表未填
  organizationType: OrganizationType;
  name: string;
  title: string | null;
  globalProfile: string | null;
  /** 大頭貼縮圖（列表/Sidebar 顯示用），失敗時 fallback 用 globalProfile */
  globalProfileThumbnail: string | null;
  setOrganizationName: React.Dispatch<React.SetStateAction<string>>;
  setOrganizationType: React.Dispatch<React.SetStateAction<OrganizationType>>;
  setName: React.Dispatch<React.SetStateAction<string>>;
  setTitle: React.Dispatch<React.SetStateAction<string | null>>;
  setGlobalProfile: React.Dispatch<React.SetStateAction<string | null>>;
  setGlobalProfileThumbnail: React.Dispatch<React.SetStateAction<string | null>>;
  handleLogout: () => void;
  /** 上傳形象照。傳入的檔案已經過裁切，選檔與裁切由 UI 層負責 */
  uploadProfileImage: (file: File) => Promise<void>;
}

const AppContext = createContext<AppContextValue | null>(null);

export const useAppContext = (): AppContextValue => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAppContext must be used within AppProvider');
  return ctx;
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [organizationName, setOrganizationName] = useState('和平里');
  const [organizationType, setOrganizationType] = useState<OrganizationType>('里');
  const [name, setName] = useState('王小明');
  const [title, setTitle] = useState<string | null>('里長');
  const [globalProfile, setGlobalProfile] = useState<string | null>(null);
  const [globalProfileThumbnail, setGlobalProfileThumbnail] = useState<string | null>(null);

  // 認證世代計數：每次登入/登出 +1。
  // 用途：API 請求發出時記下當下世代，回應到達時若世代已變，代表 session 已切換，忽略此回應，
  // 避免「登入前的舊 401 回應」誤觸發新 session 的 handleLogout。
  const authGenRef = useRef(0);
  // 防止多個 401 同時觸發 handleLogout，造成重複的 /auth/logout 請求競爭
  const isLoggingOutRef = useRef(false);

  const handleLogout = async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    authGenRef.current += 1;
    try {
      // 等 server clearCookie 完成才切換 UI，避免舊 logout 還在路上時使用者已登入成功，
      // 造成新 cookie 被剛抵達的舊 logout 清掉的競態
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setIsAuthenticated(false);
      setShowLoginPopup(true);
      isLoggingOutRef.current = false;
    }
  };

  const loadSettings = () => {
    const gen = authGenRef.current;
    fetchSettings()
      .then((settings: Settings) => {
        if (gen !== authGenRef.current) return;
        setOrganizationName(settings.organizationName ?? '');
        setOrganizationType(settings.organizationType ?? '里');
        setName(settings.name);
        setTitle(settings.title ?? null);
        setGlobalProfile(settings.globalProfileUrl ?? null);
        setGlobalProfileThumbnail(settings.globalProfileThumbnailUrl ?? null);
      })
      .catch((err: Error) => {
        if (gen !== authGenRef.current) return;
        console.error('Failed to load settings:', err);
        if (err.message.includes('401') || err.message.includes('Authentication required')) {
          handleLogout();
        }
      });
  };

  const uploadProfileImage = async (file: File) => {
    // Optimistic preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setGlobalProfile(reader.result);
        setGlobalProfileThumbnail(reader.result);
      }
    };
    reader.readAsDataURL(file);

    try {
      const updated = await updateSettingsWithImage({}, file);
      if (updated.globalProfileUrl) setGlobalProfile(updated.globalProfileUrl);
      setGlobalProfileThumbnail(updated.globalProfileThumbnailUrl ?? updated.globalProfileUrl ?? null);
    } catch {
      alert('上傳失敗，請稍後再試。');
    }
  };

  useEffect(() => {
    checkAuth()
      .then(() => {
        setIsAuthenticated(true);
        loadSettings();
      })
      .catch(() => {
        // 未帶有效 cvt → 顯示未登入通知，導回主網域登入（SSO-only）
        setShowLoginPopup(true);
      });
  }, []);

  return (
    <AppContext.Provider value={{
      isAuthenticated, showLoginPopup,
      organizationName, organizationType, name, title, globalProfile, globalProfileThumbnail,
      setOrganizationName, setOrganizationType, setName, setTitle, setGlobalProfile, setGlobalProfileThumbnail,
      handleLogout, uploadProfileImage,
    }}>
      {children}
    </AppContext.Provider>
  );
};
